import { ChangeEvent, KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { InfoCircleIcon, InputTextarea, PaperPlaneIcon } from '@repo/atomic-components';
import { GreenButton } from '@/ui/components/GreenButton';
import { InfoTooltipIcon } from '@/ui/components/InfoTooltipIcon';
import { selectMedicalMatchPanelDraftQuestion, updateMedicalMatchPanel, useAppDispatch, useAppSelector } from '@/store';
import {
  LIVE_QUESTION_MAX_LENGTH,
  LIVE_QUESTION_MIN_LENGTH,
  LIVE_QUESTION_TIPS,
  LIVE_QUESTION_TIPS_TITLE,
  MEDICAL_MATCH_DISCLAIMER,
  MEDICAL_MATCH_DISCLAIMER_TOOLTIP,
} from './constants';

type MedicalMatchQuestionComposerProps = {
  /** Case the draft is kept against while the panel is closed. */
  caseId: string | undefined;
  /**
   * Sends the question and resolves `true` once it has been accepted — the
   * field only clears on `true` so a rejected question stays editable rather
   * than forcing the user to retype it.
   */
  onAsk: (question: string) => Promise<boolean>;
  /** True for the duration of the POST that enqueues the question. */
  isAsking: boolean;
  /**
   * Set while live questions can't be asked at all — a full rerun is running,
   * the availability check hasn't come back clean, or the backend says the
   * case isn't ready. Takes the field out of use and states the reason on the
   * Ask button's hover.
   */
  blockedReason?: string;
};

export function MedicalMatchQuestionComposer({
  caseId,
  onAsk,
  isAsking,
  blockedReason,
}: MedicalMatchQuestionComposerProps) {
  const dispatch = useAppDispatch();
  // A half-typed question is put back when the panel reopens — it unmounts on
  // every sidebar tab switch. Handed to the store on the way out rather than on
  // every keystroke, so typing doesn't run every selector on the page.
  const savedDraft = useAppSelector((state) => selectMedicalMatchPanelDraftQuestion(state, caseId));
  const [question, setQuestion] = useState(savedDraft);
  const questionRef = useRef(question);
  questionRef.current = question;
  useEffect(() => {
    if (!caseId) return;
    return () => {
      dispatch(updateMedicalMatchPanel({ caseId, draftQuestion: questionRef.current }));
    };
  }, [caseId, dispatch]);
  const blockedReasonId = useId();

  const isBlocked = Boolean(blockedReason);
  const trimmedLength = question.trim().length;
  const canAsk = !isAsking && !isBlocked && trimmedLength >= LIVE_QUESTION_MIN_LENGTH;

  const handleAsk = async (): Promise<void> => {
    if (!canAsk) return;
    const wasSent = await onAsk(question.trim());
    if (wasSent) setQuestion('');
  };

  // Enter sends, Shift+Enter starts a new line — the usual chat-composer
  // split, so the button is there for discoverability rather than necessity.
  // An Enter that commits an IME composition is the user picking a word, not
  // sending the question.
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void handleAsk();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Wrapper owns the border and focus ring so the textarea, counter and
          Ask button read as a single field — same treatment as the extract
          field in AddToChronology. */}
      <div
        className={classNames(
          'focus-within:border-fletchers-gray-200 flex flex-col gap-2 rounded-lg border border-gray-300 p-[12px] transition-colors',
          isBlocked ? 'bg-fletchers-gray-50' : 'bg-white',
        )}
      >
        <InputTextarea
          value={question}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setQuestion(event.target.value)}
          onKeyDown={handleKeyDown}
          // Read-only while a question is in flight so a second can't be
          // queued, without disabling it and dropping focus; disabled outright
          // while live questions are blocked.
          readOnly={isAsking}
          disabled={isBlocked}
          maxLength={LIVE_QUESTION_MAX_LENGTH}
          rows={2}
          placeholder="Type your question here..."
          aria-label="Ask a question about this case"
          aria-describedby={isBlocked ? blockedReasonId : undefined}
          className="w-full resize-none !rounded-none !border-0 !p-0 !text-[14px] !shadow-none !ring-0 !outline-none placeholder:!text-[14px] focus:!border-0 focus:!shadow-none focus:!ring-0"
        />
        <div className="flex items-center justify-between gap-2">
          {/* Counts up to the minimum the backend accepts (trimmed, as it
              measures) and drops away once it's met; the Ask button and Enter
              stay inert until then. The span stays put so the controls on the
              right don't shift when it empties. */}
          <span className="text-fletchers-gray-200 text-[12px]">
            {trimmedLength < LIVE_QUESTION_MIN_LENGTH && `${trimmedLength}/${LIVE_QUESTION_MIN_LENGTH}`}
          </span>
          <div className="flex items-center gap-2">
            {/* Right-anchored: the icon sits a stone's throw from the panel
                edge, and the tooltip is laid out even while hidden — opening
                it rightwards pushes the sidebar into horizontal scroll. */}
            <InfoTooltipIcon
              align="right"
              panelClassName="w-[320px]"
              tooltip={
                <>
                  <p className="text-fletchers-black mb-2 font-semibold">{LIVE_QUESTION_TIPS_TITLE}</p>
                  <ul className="text-fletchers-gray-200 marker:text-fletchers-green-600 list-disc space-y-2 pl-4">
                    {LIVE_QUESTION_TIPS.map((tip) => (
                      <li key={tip}>{tip}</li>
                    ))}
                  </ul>
                </>
              }
            />
            {/* Hover sits on the wrapper rather than the button so the reason
                still opens while the button is disabled — which is exactly
                when it matters. Opens upwards because the composer is pinned
                to the bottom of the panel. */}
            <div className="group relative">
              <GreenButton
                label="Ask"
                icon={<PaperPlaneIcon className="h-[12px] w-[12px]" />}
                loading={isAsking}
                disabled={!canAsk}
                onClick={handleAsk}
                aria-describedby={isBlocked ? blockedReasonId : undefined}
                className="!h-[32px] !text-[14px]"
              />
              {blockedReason && (
                <div
                  id={blockedReasonId}
                  role="tooltip"
                  className="pointer-events-none invisible absolute right-0 bottom-full z-[999999] mb-1 w-[220px] rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm whitespace-pre-line text-black opacity-0 shadow-xl transition-opacity duration-100 group-hover:visible group-hover:opacity-100"
                >
                  {blockedReason}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="group text-fletchers-gray-200 relative flex cursor-pointer items-center justify-center gap-1.5 text-[12px]">
        <InfoCircleIcon className="h-[14px] w-[14px] shrink-0" />
        <span>{MEDICAL_MATCH_DISCLAIMER}</span>
        <div
          role="tooltip"
          className="pointer-events-none invisible absolute bottom-full left-1/2 z-[999999] mb-1 w-[280px] -translate-x-1/2 rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm whitespace-pre-line text-black opacity-0 shadow-xl transition-opacity duration-100 group-hover:visible group-hover:opacity-100"
        >
          {MEDICAL_MATCH_DISCLAIMER_TOOLTIP}
        </div>
      </div>
    </div>
  );
}
